/**
 * `entities/project-comment` API surface.
 *
 * Exposes:
 *   - Service functions for READING comments + replies (target-scoped).
 *   - `*Query` queryOptions helpers for TanStack consumers.
 *
 * Comments attach to a `target` = `{ targetType: 'file' | 'brief'; targetId }`.
 * File comments additionally carry a `versionId`.
 */
import { CommentsService } from '@inference.engine/workspace-api-client/web';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { workspaceHttp } from '@/lib/api/http-client';

import { PROJECT_COMMENT_QUERY_KEYS, type CommentTargetType } from './query-keys';
import type { ProjectCommentsResponse } from '../model/types';

const COMMENTS_PAGE_SIZE = 50;

/** Reference to the entity a comment thread is scoped to. */
export interface CommentTargetRef {
  targetType: CommentTargetType;
  targetId: string;
}

// ──────────────────────────── Service functions ────────────────────────────

interface ListCommentsArgs {
  projectId: string;
  target: CommentTargetRef;
  page: number;
  versionId?: string;
  rootSortOrder?: 'asc' | 'desc';
  includeResolved?: boolean;
}

export const listComments = async (args: ListCommentsArgs): Promise<ProjectCommentsResponse> => {
  const {
    projectId,
    target,
    page,
    versionId,
    rootSortOrder = 'asc',
    includeResolved = false,
  } = args;
  const response = await CommentsService.listByTarget({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    query: {
      targetType: target.targetType,
      targetId: target.targetId,
      page,
      pageSize: COMMENTS_PAGE_SIZE,
      ...(versionId ? { versionId } : {}),
      rootSortOrder,
      includeResolved,
    },
  });

  if (!response.data) {
    throw new Error('Failed to list comments');
  }

  return response.data;
};

/** A single active annotation (highlight anchor) tied to a comment. */
export interface CommentAnnotationRef {
  commentId: string;
  annotation: Record<string, unknown>;
}

export const listCommentAnnotations = async (
  projectId: string,
  target: CommentTargetRef,
): Promise<CommentAnnotationRef[]> => {
  const response = await CommentsService.listAnnotations({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    query: {
      targetType: target.targetType,
      targetId: target.targetId,
    },
  });

  if (!response.data) {
    throw new Error('Failed to list comment annotations');
  }

  return response.data.items;
};

export const listCommentReplies = async (
  projectId: string,
  target: CommentTargetRef,
  parentId: string,
): Promise<ProjectCommentsResponse> => {
  const response = await CommentsService.listByTarget({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    query: {
      targetType: target.targetType,
      targetId: target.targetId,
      parentId,
      pageSize: COMMENTS_PAGE_SIZE,
    },
  });

  if (!response.data) {
    throw new Error('Failed to list comment replies');
  }

  return response.data;
};

// ──────────────────────────── queryOptions helpers ─────────────────────────

interface ProjectCommentsInfiniteQueryArgs {
  projectId: string | undefined;
  target: CommentTargetRef | undefined;
  versionId?: string;
  rootSortOrder?: 'asc' | 'desc';
  showResolved?: boolean;
}

export const projectCommentsInfiniteQuery = (args: ProjectCommentsInfiniteQueryArgs) => {
  const { projectId, target, versionId, rootSortOrder = 'asc', showResolved = true } = args;
  const targetType = target?.targetType ?? 'file';
  const targetId = target?.targetId ?? '';
  const enabled = !!projectId && !!targetId && (targetType === 'file' ? !!versionId : true);

  return infiniteQueryOptions({
    queryKey: PROJECT_COMMENT_QUERY_KEYS.listSorted(
      projectId ?? '',
      targetType,
      targetId,
      versionId,
      rootSortOrder,
      showResolved,
    ),
    queryFn: ({ pageParam }) =>
      listComments({
        projectId: projectId!,
        target: { targetType, targetId },
        page: pageParam,
        versionId,
        rootSortOrder,
        includeResolved: showResolved,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.total / lastPage.pageSize);
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
    },
    enabled,
  });
};

export const commentRepliesQuery = (
  projectId: string | undefined,
  target: CommentTargetRef | undefined,
  parentId: string,
) => {
  const targetType = target?.targetType ?? 'file';
  const targetId = target?.targetId ?? '';
  return queryOptions({
    queryKey: PROJECT_COMMENT_QUERY_KEYS.replies(projectId ?? '', targetType, targetId, parentId),
    queryFn: () => listCommentReplies(projectId!, { targetType, targetId }, parentId),
    enabled: !!projectId && !!targetId,
  });
};

export const commentAnnotationsQuery = (
  projectId: string | undefined,
  target: CommentTargetRef | undefined,
) => {
  const targetType = target?.targetType ?? 'file';
  const targetId = target?.targetId ?? '';
  return queryOptions({
    queryKey: PROJECT_COMMENT_QUERY_KEYS.annotations(projectId ?? '', targetType, targetId),
    queryFn: () => listCommentAnnotations(projectId!, { targetType, targetId }),
    enabled: !!projectId && !!targetId,
  });
};
